<script setup lang="ts">
import { config } from "@/lib/variantConfig";
import { getCustomCtas } from "@/repository";
import { ButtonTarget, CtaType } from "@/types/cta";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { useAppSettingsStore } from "@/stores/appSettingsStore";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    allowedTypes?: CtaType[];
  }>(),
  {
    allowedTypes: () => [CtaType.Network, CtaType.Renewal, CtaType.Subscribe],
  }
);

const authStore = useAuthStore();
const appSettingsStore = useAppSettingsStore();
const { t } = useI18n();

const { data } = useQuery({
  queryKey: ["custom-ctas"],
  queryFn: () => getCustomCtas(0),
});

const subscribeUrl = computed(() => {
  const settings = appSettingsStore.settings;
  return authStore.user
    ? settings?.url_subscribe
    : settings?.url_register_and_subscribe;
});

const cta = computed(() => {
  if (!data.value) return null;
  const networkCtas = data.value?.data.data;
  if (props.allowedTypes.includes(CtaType.Network) && networkCtas.length > 0) return networkCtas[0];
  if (props.allowedTypes.includes(CtaType.Renewal) && authStore.subscriptionExpiringSoon && subscribeUrl.value) {
    return {
      title: t("renewal_cta.title", [config.strings.app_name]),
      text: t("renewal_cta.description", [authStore.daysUntilSubscriptionExpiry]),
      button_text: t("renewal_cta.button"),
      button_action: subscribeUrl.value,
      button_target: ButtonTarget.BrowserInternal,
    };
  }
  if (props.allowedTypes.includes(CtaType.Subscribe) && !authStore.hasActiveSubscription && subscribeUrl.value) {
    return {
      title: t("subscription_cta.title", [config.strings.app_name]),
      text: t("subscription_cta.description"),
      button_text: t("subscription_cta.button"),
      button_action: subscribeUrl.value,
      button_target: ButtonTarget.BrowserInternal,
    };
  }
  return null;
});
</script>

<template>
  <Transition>
    <div v-if="cta" class="bg-background-brand text-text-white rounded-lg p-4">
      <h4 class="font-black mb-1">
        {{ cta.title }}
      </h4>
      <p class="opacity-80 mb-6">
        {{ cta.text }}
      </p>
      <a
        class="btn-white"
        :target="
          cta.button_target === ButtonTarget.BrowserExternal
            ? '_blank'
            : '_self'
        "
        :href="cta.button_action"
      >
        {{ cta.button_text }}
      </a>
    </div>
  </Transition>
</template>
